import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';
import {
  Users, Feather, BookOpen, Star, ChartBar, ShieldCheck, Plus, PencilSimple,
  Trash, Eye, EyeSlash, ArrowUpRight, MagnifyingGlass, Upload, X,
  Tag, ClipboardText, Warning, SquaresFour, Gear, Bell, FolderOpen, FileArrowUp,
  List as ListIcon, CreditCard, Database, CloudArrowUp, Article, Plug, Envelope, Trophy
} from '@phosphor-icons/react';
import CmsDashboard from '../components/cms/CmsDashboard';
import CmsUsers from '../components/cms/CmsUsers';
import CmsModeration from '../components/cms/CmsModeration';
import CmsCategories from '../components/cms/CmsCategories';
import CmsActivity from '../components/cms/CmsActivity';
import CmsHomepage from '../components/cms/CmsHomepage';
import CmsContentQuality from '../components/cms/CmsContentQuality';
import CmsSiteSettings from '../components/cms/CmsSiteSettings';
import CmsNotifications from '../components/cms/CmsNotifications';
import CmsMediaLibrary from '../components/cms/CmsMediaLibrary';
import CmsCompetitions from '../components/cms/CmsCompetitions';
import CmsBulkImport from '../components/cms/CmsBulkImport';
import ProfessionalMenuBuilder from '../components/cms/ProfessionalMenuBuilder';
import CmsSeoManager from '../components/cms/CmsSeoManager';
import CmsPushNotifications from '../components/cms/CmsPushNotifications';
import CmsPaymentSettings from '../components/cms/CmsPaymentSettings';
import CmsBackupRestore from '../components/cms/CmsBackupRestore';
import CmsStorageManager from '../components/cms/CmsStorageManager';
import CmsCopyrightTracker from '../components/cms/CmsCopyrightTracker';
import CmsPages from '../components/cms/CmsPages';
import CmsContentPermissions from '../components/cms/CmsContentPermissions';
import CmsUserManagement from '../components/cms/CmsUserManagement';
import CmsIntegrations from '../components/cms/CmsIntegrations';
import CmsContactMessages from '../components/cms/CmsContactMessages';

const WORK_TYPES = ['poem', 'story', 'short_story', 'novel', 'essay', 'ghazal', 'doha', 'bhajan', 'shayari', 'chaupai', 'play', 'memoir', 'letter', 'quote'];
const PERIODS = ['Bhakti Movement', 'Riti Kaal', 'Bharatendu Yug', 'Chhayavaad', 'Progressive', 'Modern', 'Contemporary'];

const NAV_GROUPS = [
  {
    label: 'Insights',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: SquaresFour },
      { key: 'notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    label: 'Content',
    items: [
      { key: 'homepage', label: 'Homepage', icon: SquaresFour },
      { key: 'pages', label: 'Pages', icon: Article },
      { key: 'authors', label: 'Authors', icon: Feather },
      { key: 'works', label: 'Works', icon: BookOpen },
      { key: 'competitions', label: 'Competitions', icon: Trophy },
      { key: 'featured', label: 'Featured', icon: Star },
      { key: 'categories', label: 'Categories', icon: Tag },
    ],
  },
  {
    label: 'Community',
    items: [
      { key: 'users', label: 'Users', icon: Users },
      { key: 'user-management', label: 'Role Management', icon: ShieldCheck },
      { key: 'contact-messages', label: 'Contact Messages', icon: Envelope },
      { key: 'moderation', label: 'Moderation', icon: Warning },
    ],
  },
  {
    label: 'System',
    items: [
      { key: 'permissions', label: 'Content Permissions', icon: ShieldCheck },
      { key: 'integrations', label: 'Integrations', icon: Plug },
      { key: 'quality', label: 'Content Quality', icon: ShieldCheck },
      { key: 'media', label: 'Media Library', icon: FolderOpen },
      { key: 'bulk-import', label: 'Bulk Import', icon: FileArrowUp },
      { key: 'menus', label: 'Menu Builder', icon: ListIcon },
      { key: 'seo', label: 'SEO & Redirects', icon: MagnifyingGlass },
      { key: 'push', label: 'Push Notifications', icon: Bell },
      { key: 'payments', label: 'Payment Settings', icon: CreditCard },
      { key: 'backup', label: 'Backup & Restore', icon: Database },
      { key: 'storage', label: 'Storage Manager', icon: CloudArrowUp },
      { key: 'copyright', label: 'Copyright Tracker', icon: ShieldCheck },
      { key: 'settings', label: 'Site Settings', icon: Gear },
      { key: 'activity', label: 'Activity Log', icon: ClipboardText },
    ],
  },
];

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.role !== 'editor')) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/overview`, { withCredentials: true })
      .then((r) => setStats(r.data.totals))
      .catch(() => {});
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      <div className="flex justify-center py-32"><div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-16 z-30 h-[calc(100vh-4rem)] w-64 shrink-0 border-r border-border bg-card overflow-y-auto transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          data-testid="cms-sidebar"
        >
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} weight="duotone" className="text-primary" />
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Admin Console</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Kavyalekh CMS</p>
              </div>
            </div>
          </div>
          <nav className="p-3 space-y-4">
            {NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 mb-2">{group.label}</p>
                <ul className="space-y-0.5">
                  {group.items.map(({ key, label, icon: Icon }) => (
                    <li key={key}>
                      <button
                        onClick={() => { setTab(key); setSidebarOpen(false); }}
                        data-testid={`admin-tab-${key}`}
                        className={`w-full text-left inline-flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${tab === key ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                      >
                        <Icon size={16} weight={tab === key ? 'duotone' : 'regular'} />
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-background/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Mobile menu toggle */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 text-sm mb-4 px-3 py-2 border border-border rounded-lg"
              data-testid="cms-sidebar-toggle"
            >
              <ShieldCheck size={16} /> Menu
            </button>

            {tab === 'dashboard' && <CmsDashboard />}
            {tab === 'notifications' && <CmsNotifications />}
            {tab === 'homepage' && <CmsHomepage />}
            {tab === 'pages' && <CmsPages />}
            {tab === 'authors' && <AuthorsTab />}
            {tab === 'works' && <WorksTab />}
            {tab === 'competitions' && <CmsCompetitions />}
            {tab === 'featured' && <FeaturedTab />}
            {tab === 'categories' && <CmsCategories />}
            {tab === 'users' && <CmsUsers />}
            {tab === 'user-management' && <CmsUserManagement />}
            {tab === 'contact-messages' && <CmsContactMessages />}
            {tab === 'moderation' && <CmsModeration />}
            {tab === 'permissions' && <CmsContentPermissions />}
            {tab === 'integrations' && <CmsIntegrations />}
            {tab === 'quality' && <CmsContentQuality />}
            {tab === 'media' && <CmsMediaLibrary />}
            {tab === 'bulk-import' && <CmsBulkImport />}
            {tab === 'menus' && <ProfessionalMenuBuilder />}
            {tab === 'seo' && <CmsSeoManager />}
            {tab === 'push' && <CmsPushNotifications />}
            {tab === 'payments' && <CmsPaymentSettings />}
            {tab === 'backup' && <CmsBackupRestore />}
            {tab === 'storage' && <CmsStorageManager />}
            {tab === 'copyright' && <CmsCopyrightTracker />}
            {tab === 'settings' && <CmsSiteSettings />}
            {tab === 'activity' && <CmsActivity />}
          </div>
        </main>
      </div>
    </div>
  );
}

function OverviewTab({ stats }) {
  if (!stats) return <div className="animate-pulse text-muted-foreground">Loading…</div>;
  const cards = [
    { label: 'Authors', value: stats.authors, sub: `${stats.authors_published} published`, icon: Feather },
    { label: 'Legendary Works', value: stats.works, sub: `${stats.works_featured} featured`, icon: BookOpen },
    { label: 'Community Articles', value: stats.articles, sub: 'published', icon: BookOpen },
    { label: 'Users', value: stats.users, sub: 'registered', icon: Users },
    { label: 'Reviews', value: stats.reviews, sub: 'submitted', icon: Star },
    { label: 'Featured Authors', value: stats.authors_featured, sub: 'on homepage', icon: Star },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-overview">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
              <Icon size={14} /> {c.label}
            </div>
            <p className="text-3xl font-bold">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
          </div>
        );
      })}
    </div>
  );
}

function AuthorsTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // author or 'new'
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300' });
      if (search) params.set('search', search);
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors?${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.name}" AND all their works? This cannot be undone.`)) return;
    await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors/${a.author_id}?cascade=true`, { withCredentials: true });
    toast.success('Deleted');
    load();
  };
  const togglePublish = async (a) => {
    const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors/${a.author_id}/publish`, {}, { withCredentials: true });
    toast.success(r.data.is_published ? 'Published' : 'Unpublished');
    load();
  };
  const toggleFeature = async (a) => {
    const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors/${a.author_id}/feature`, {}, { withCredentials: true });
    toast.success(r.data.is_featured ? 'Featured' : 'Unfeatured');
    load();
  };

  if (editing) return <AuthorForm initial={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search authors…" data-testid="admin-authors-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-full outline-none focus:border-primary" />
        </div>
        <button onClick={() => setEditing('new')} data-testid="admin-new-author" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
          <Plus size={14} /> New Author
        </button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="admin-authors-list">
          {items.map((a) => (
            <div key={a.author_id} className="p-4 flex items-center gap-3" data-testid={`admin-author-${a.author_id}`}>
              <img src={a.photograph} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate flex items-center gap-2">
                  {a.name}
                  {a.is_featured && <Star size={14} weight="fill" className="text-amber-500" />}
                  {a.is_published === false && <span className="text-xs uppercase tracking-widest text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Unpublished</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{a.literary_movement || a.literary_period || '—'} · {a.works_count} works</p>
              </div>
              <Link to={`/author/${a.slug}`} target="_blank" data-testid={`admin-preview-author-${a.author_id}`} className="text-muted-foreground hover:text-foreground p-2" title="Preview"><ArrowUpRight size={16} /></Link>
              <button onClick={() => toggleFeature(a)} data-testid={`admin-feature-author-${a.author_id}`} className="text-muted-foreground hover:text-foreground p-2" title="Toggle featured"><Star size={16} weight={a.is_featured ? 'fill' : 'regular'} /></button>
              <button onClick={() => togglePublish(a)} data-testid={`admin-publish-author-${a.author_id}`} className="text-muted-foreground hover:text-foreground p-2" title="Toggle publish">{a.is_published === false ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => setEditing(a)} data-testid={`admin-edit-author-${a.author_id}`} className="text-muted-foreground hover:text-primary p-2"><PencilSimple size={16} /></button>
              <button onClick={() => del(a)} data-testid={`admin-delete-author-${a.author_id}`} className="text-muted-foreground hover:text-red-600 p-2"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuthorForm({ initial, onDone, onCancel }) {
  const [form, setForm] = useState(initial || {
    name: '', pen_name: '', biography: '', birth_date: '', death_date: '', birth_place: '',
    literary_period: '', literary_movement: '', languages: [], genres: [], themes: [],
    occupation: [], awards: [], famous_quotes: [], writing_style: '', influences: [], influenced_by: [],
    photograph: '', cover_image: '', author_type: 'legendary', is_featured: false, is_published: true,
    // New fields for simplified navigation
    is_legendary: false,
    is_verified: false,
    is_award_winner: false,
    century: '',
    birth_year: null,
    death_year: null,
    state: '',
    country: 'India',
    gender: '',
    is_public_domain: false,
  });
  const [saving, setSaving] = useState(false);
  const asArray = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
  const arrayStr = (a) => (a || []).join(', ');

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (initial) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors/${initial.author_id}`, form, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors`, form, { withCredentials: true });
        toast.success('Created');
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">{label}</label>
      {type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} rows={4} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid={`author-field-${key}`} />
      ) : (
        <input type={type} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: type === 'number' ? (e.target.value ? parseInt(e.target.value) : null) : e.target.value })} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid={`author-field-${key}`} />
      )}
    </div>
  );
  const arrayField = (label, key) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">{label} <span className="normal-case text-[10px] text-muted-foreground">(comma-separated)</span></label>
      <input value={arrayStr(form[key])} onChange={(e) => setForm({ ...form, [key]: asArray(e.target.value) })} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid={`author-array-${key}`} />
    </div>
  );

  return (
    <form onSubmit={save} className="bg-card border border-border rounded-lg p-6 space-y-6" data-testid="author-form">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-semibold">{initial ? `Edit ${initial.name}` : 'Create New Author'}</h3>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
      </div>

      {/* Basic Info */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Name', 'name')}
          {field('Pen Name', 'pen_name')}
          {field('Birth Date', 'birth_date')}
          {field('Death Date', 'death_date')}
          {field('Birth Place', 'birth_place')}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Gender</label>
            <select value={form.gender || ''} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid="author-field-gender">
              <option value="">— select —</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Literary Info */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Literary Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Literary Period</label>
            <select value={form.literary_period || ''} onChange={(e) => setForm({ ...form, literary_period: e.target.value })} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid="author-field-literary_period">
              <option value="">— select —</option>
              {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {field('Literary Movement', 'literary_movement')}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Century</label>
            <select value={form.century || ''} onChange={(e) => setForm({ ...form, century: e.target.value })} className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" data-testid="author-field-century">
              <option value="">— select —</option>
              {['13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th', '21st'].map((c) => <option key={c} value={c}>{c} Century</option>)}
            </select>
          </div>
          {field('Writing Style', 'writing_style')}
          {field('Birth Year', 'birth_year', 'number')}
          {field('Death Year', 'death_year', 'number')}
        </div>
      </div>

      {field('Biography', 'biography', 'textarea')}

      {/* Location & Demographics */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Location</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('State', 'state')}
          {field('Country', 'country')}
        </div>
      </div>

      {/* Categories */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Categories & Tags</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {arrayField('Genres', 'genres')}
          {arrayField('Themes', 'themes')}
          {arrayField('Languages', 'languages')}
          {arrayField('Occupation', 'occupation')}
          {arrayField('Awards', 'awards')}
          {arrayField('Influences', 'influences')}
          {arrayField('Influenced by', 'influenced_by')}
          {arrayField('Famous Quotes', 'famous_quotes')}
        </div>
      </div>

      {/* Media */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Media</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Photograph URL', 'photograph')}
          {field('Cover Image URL', 'cover_image')}
        </div>
      </div>

      {/* Attributes */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-primary">Attributes</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_legendary} onChange={(e) => setForm({ ...form, is_legendary: e.target.checked })} data-testid="author-field-legendary" className="rounded" />
            <span>🏛️ Legendary</span>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_verified} onChange={(e) => setForm({ ...form, is_verified: e.target.checked })} data-testid="author-field-verified" className="rounded" />
            <span>✓ Verified</span>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="author-field-featured" className="rounded" />
            <span>⭐ Featured</span>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_award_winner} onChange={(e) => setForm({ ...form, is_award_winner: e.target.checked })} data-testid="author-field-award-winner" className="rounded" />
            <span>🏆 Award Winner</span>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_public_domain} onChange={(e) => setForm({ ...form, is_public_domain: e.target.checked })} data-testid="author-field-public-domain" className="rounded" />
            <span>📜 Public Domain</span>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.is_published !== false} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="author-field-published" className="rounded" />
            <span>👁️ Published</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full border border-border text-sm">Cancel</button>
        <button type="submit" disabled={saving} data-testid="author-form-submit" className="px-6 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">{saving ? 'Saving…' : (initial ? 'Save changes' : 'Create Author')}</button>
      </div>
    </form>
  );
}

function WorksTab() {
  const [authors, setAuthors] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors?limit=300`).then((r) => setAuthors(r.data));
  }, []);

  const load = async () => {
    if (!selectedAuthor) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({});
      if (type) params.set('work_type', type);
      if (search) params.set('search', search);
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/${selectedAuthor}/works?${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [selectedAuthor, type, search]);

  const del = async (w) => {
    if (!window.confirm(`Delete "${w.title}"?`)) return;
    await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/works/${w.work_id}`, { withCredentials: true });
    toast.success('Deleted'); load();
  };
  const feature = async (w) => {
    const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/works/${w.work_id}/feature`, {}, { withCredentials: true });
    toast.success(r.data.is_featured ? 'Featured' : 'Unfeatured'); load();
  };

  if (editing) return <WorkForm initial={editing === 'new' ? null : editing} authors={authors} defaultAuthorSlug={selectedAuthor} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <select value={selectedAuthor} onChange={(e) => setSelectedAuthor(e.target.value)} data-testid="admin-works-author-select"
          className="text-sm px-3 py-2 bg-card border border-border rounded-full outline-none focus:border-primary">
          <option value="">— Select author —</option>
          {authors.map((a) => <option key={a.author_id} value={a.slug}>{a.name}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-works-type-select"
          className="text-sm px-3 py-2 bg-card border border-border rounded-full outline-none focus:border-primary">
          <option value="">All types</option>
          {WORK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" data-testid="admin-works-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-full outline-none focus:border-primary" />
        </div>
        <button onClick={() => setEditing('new')} disabled={!selectedAuthor && authors.length === 0} data-testid="admin-new-work"
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
          <Plus size={14} /> New Work
        </button>
      </div>

      {!selectedAuthor ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center text-sm text-muted-foreground">Select an author to see their works.</div>
      ) : loading ? <div className="text-muted-foreground">Loading…</div> : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="admin-works-list">
          {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No works. Click "New Work" to add.</div>}
          {items.map((w) => (
            <div key={w.work_id} className="p-4 flex items-center gap-3" data-testid={`admin-work-${w.work_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate flex items-center gap-2">
                  {w.title}
                  {w.is_featured && <Star size={14} weight="fill" className="text-amber-500" />}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  <span className="uppercase tracking-widest">{w.work_type}</span>
                  {w.year ? ` · ${w.year}` : ''} · {w.views} reads · {w.likes} likes
                </p>
              </div>
              <Link to={`/work/${w.work_id}`} target="_blank" className="text-muted-foreground hover:text-foreground p-2" title="Preview"><ArrowUpRight size={16} /></Link>
              <button onClick={() => feature(w)} data-testid={`admin-feature-work-${w.work_id}`} className="text-muted-foreground hover:text-amber-500 p-2" title="Feature"><Star size={16} weight={w.is_featured ? 'fill' : 'regular'} /></button>
              <button onClick={() => setEditing(w)} data-testid={`admin-edit-work-${w.work_id}`} className="text-muted-foreground hover:text-primary p-2"><PencilSimple size={16} /></button>
              <button onClick={() => del(w)} data-testid={`admin-delete-work-${w.work_id}`} className="text-muted-foreground hover:text-red-600 p-2"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkForm({ initial, authors, defaultAuthorSlug, onDone, onCancel }) {
  const [form, setForm] = useState(initial || {
    author_slug: defaultAuthorSlug || '', title: '', title_hindi: '', content: '',
    work_type: 'poem', year: '', description: '', tags: [], themes: [],
    is_public_domain: true, downloadable: true, is_featured: false,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return toast.error('Title and content required');
    setSaving(true);
    try {
      if (initial) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/works/${initial.work_id}`, form, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/works`, form, { withCredentials: true });
        toast.success('Created');
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="work-form">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-semibold">{initial ? `Edit "${initial.title}"` : 'Create New Work'}</h3>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {!initial && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Author</label>
            <select value={form.author_slug || ''} onChange={(e) => setForm({ ...form, author_slug: e.target.value })} data-testid="work-field-author"
              className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary">
              <option value="">— select —</option>
              {authors.map((a) => <option key={a.author_id} value={a.slug}>{a.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Type</label>
          <select value={form.work_type} onChange={(e) => setForm({ ...form, work_type: e.target.value })} data-testid="work-field-type"
            className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary">
            {WORK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Title (English)</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="work-field-title" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Title (Devanagari)</label>
          <input value={form.title_hindi || ''} onChange={(e) => setForm({ ...form, title_hindi: e.target.value })} data-testid="work-field-title-hindi" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Year</label>
          <input value={form.year || ''} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="work-field-year" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Cover Image URL</label>
          <input value={form.cover_image || ''} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="work-field-cover" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Description</label>
        <input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="work-field-description" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Content (Hindi/Urdu)</label>
        <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} data-testid="work-field-content"
          className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary font-mono" style={{ fontFamily: 'Mukta, monospace' }} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Tags <span className="normal-case text-[10px]">(comma-separated)</span></label>
          <input value={(form.tags || []).join(', ')} onChange={(e) => setForm({ ...form, tags: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} data-testid="work-field-tags" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Themes <span className="normal-case text-[10px]">(comma-separated)</span></label>
          <input value={(form.themes || []).join(', ')} onChange={(e) => setForm({ ...form, themes: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} data-testid="work-field-themes" className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.is_public_domain} onChange={(e) => setForm({ ...form, is_public_domain: e.target.checked })} data-testid="work-field-public-domain" /> Public domain</label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.downloadable} onChange={(e) => setForm({ ...form, downloadable: e.target.checked })} data-testid="work-field-downloadable" /> Downloadable</label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="work-field-featured" /> Featured</label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full border border-border text-sm">Cancel</button>
        <button type="submit" disabled={saving} data-testid="work-form-submit" className="px-6 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">{saving ? 'Saving…' : (initial ? 'Save changes' : 'Create Work')}</button>
      </div>
    </form>
  );
}

function FeaturedTab() {
  const [works, setWorks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const [wr, ar] = await Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/works/featured?limit=50`),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors?limit=300`)
    ]);
    setWorks(wr.data);
    setAuthors(ar.data.filter(a => a.is_featured));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const unfeature = async (w) => {
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/works/${w.work_id}/feature`, {}, { withCredentials: true });
    load();
  };
  const unfeatureAuthor = async (a) => {
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/authors/${a.author_id}/feature`, {}, { withCredentials: true });
    load();
  };
  if (loading) return <div>Loading…</div>;
  return (
    <div className="space-y-8" data-testid="featured-tab">
      <div>
        <h3 className="text-xl font-semibold mb-3">Featured Authors ({authors.length})</h3>
        {authors.length === 0 ? <p className="text-sm text-muted-foreground">No featured authors. Feature some from the Authors tab.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {authors.map((a) => (
              <div key={a.author_id} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                <img src={a.photograph} alt="" className="w-12 h-12 rounded-full object-cover" />
                <div className="flex-1 min-w-0"><p className="font-semibold truncate">{a.name}</p></div>
                <button onClick={() => unfeatureAuthor(a)} className="text-amber-500 p-2"><Star size={16} weight="fill" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-3">Featured Works ({works.length})</h3>
        {works.length === 0 ? <p className="text-sm text-muted-foreground">No featured works. Feature some from the Works tab.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {works.map((w) => (
              <div key={w.work_id} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{w.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{w.work_type} · {w.author?.name}</p>
                </div>
                <button onClick={() => unfeature(w)} className="text-amber-500 p-2"><Star size={16} weight="fill" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BulkImportTab() {
  const [json, setJson] = useState('{\n  "authors": [\n    {\n      "name": "Example Poet",\n      "biography": "Short bio",\n      "literary_period": "Modern"\n    }\n  ],\n  "works": [\n    {\n      "author_slug": "example-poet",\n      "title": "Example Poem",\n      "content": "अपनी कविता यहाँ",\n      "work_type": "poem",\n      "year": "2020"\n    }\n  ]\n}');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const run = async () => {
    let payload;
    try {
      payload = JSON.parse(json);
    } catch (e) {
      return toast.error('Invalid JSON: ' + e.message);
    }
    setRunning(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-import`, payload, { withCredentials: true });
      setResult(r.data);
      toast.success(`Imported ${r.data.authors_created} authors, ${r.data.works_created} works`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setRunning(false); }
  };
  return (
    <div className="space-y-4" data-testid="bulk-import-tab">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm">
        <p className="font-semibold mb-2">Bulk import authors + works via JSON</p>
        <p className="text-muted-foreground">Paste a JSON blob with <code className="text-xs bg-muted px-1">authors</code> and/or <code className="text-xs bg-muted px-1">works</code> arrays. Works reference their author via <code className="text-xs bg-muted px-1">author_slug</code>. Duplicates by title/author are skipped.</p>
      </div>
      <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={18} data-testid="bulk-import-input"
        className="w-full text-sm px-3 py-2 bg-card border border-border rounded outline-none focus:border-primary font-mono" />
      <button onClick={run} disabled={running} data-testid="bulk-import-submit" className="inline-flex items-center gap-1 px-6 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
        <Upload size={14} /> {running ? 'Importing…' : 'Import'}
      </button>
      {result && (
        <div className="bg-card border border-border rounded-lg p-4 text-sm" data-testid="bulk-import-result">
          <p>✓ Authors created: <strong>{result.authors_created}</strong> · skipped: {result.authors_skipped}</p>
          <p>✓ Works created: <strong>{result.works_created}</strong> · skipped: {result.works_skipped}</p>
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-red-600 text-xs">
              {result.errors.map((err, i) => <li key={i}>• {err}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
